import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Main from './component/Navigations/Main';
import Loader from './component/Loader';
import BlockUi from './component/shared/BlockUI';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { unregister } from './registerServiceWorker';
import { getCannyToken } from './api/CannyApi';

import './scss/style.scss';

unregister();

// keys are not sensitive
const AMPLITUDE_API_KEY = 'f0b921c25528eb31f25526733d6a7ef8';
const SENTRY_API_KEY = 'c4fa6a087dbe41b8acaaa38990c92a1f';

const BANNER_SELECTOR = '.amplitude-engagement-banner-container';
const BANNER_HEIGHT_VAR = '--amplitude-banner-height';
const SESSION_REPLAY_SAMPLE_RATE = 0.5;
const AMPLITUDE_SCRIPT_URL = `https://cdn.amplitude.com/script/${AMPLITUDE_API_KEY}.js`;
const AMPLITUDE_ENGAGEMENT_SCRIPT_URL = `https://cdn.amplitude.com/script/${AMPLITUDE_API_KEY}.engagement.js`;

const loadScript = (src, onLoad, onError) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = onLoad;
    script.onerror = onError;
    document.head.appendChild(script);
};

const identifyUser = properties => {
    const identifyEvent = new window.amplitude.Identify();

    Object.keys(properties).forEach(key => {
        if (properties[key] === undefined) {
            identifyEvent.unset(key);
        } else {
            identifyEvent.set(key, properties[key]);
        }
    });

    window.amplitude.identify(identifyEvent);
};

const isBanner = node =>
    node.nodeType === Node.ELEMENT_NODE &&
    (node.matches(BANNER_SELECTOR) || !!node.querySelector(BANNER_SELECTOR));

const touchesBanner = mutation =>
    Array.prototype.some.call(mutation.addedNodes, isBanner) ||
    Array.prototype.some.call(mutation.removedNodes, isBanner);

export class App extends Component {
    static propTypes = {
        isApiCallInProgress: PropTypes.bool.isRequired,
    };

    constructor(props) {
        super(props);
        this.initSentry = this.initSentry.bind(this);
        this.initSentry();

        this.syncAmplitude = this.syncAmplitude.bind(this);
        this.initCanny = this.initCanny.bind(this);
        this.observeAmplitudeBanner = this.observeAmplitudeBanner.bind(this);
        this.getUserId = this.getUserId.bind(this);
        this._amplitudeStarted = false;
        this._amplitudeSdkRequested = false;
        this._amplitudeOptedOut = false;
        this._amplitudeUserId = undefined;
        this._amplitudeContext = undefined;
        this._observedBanner = null;
        this._bannerHeight = undefined;
        this._cannyButtons = new Set();
        this._cannyToken = undefined;
        this._cannyPending = false;
    }

    initSentry() {
        if (
            !window.location.href.includes('appdev') &&
            !window.location.href.includes('app-staging')
        ) {
            Sentry.init({
                dsn: `https://${SENTRY_API_KEY}@o1170723.ingest.sentry.io/6264519`,
                integrations: [new BrowserTracing()],
                environment: process.env.NODE_ENV,

                // Set tracesSampleRate to 1.0 to capture 100%
                // of transactions for performance monitoring.
                // We recommend adjusting this value in production
                tracesSampleRate: 1.0,
                ignoreErrors: [
                    'ResizeObserver loop limit exceeded',
                    'ResizeObserver loop completed with undelivered notifications.',
                    'Non-Error exception captured',
                    'Non-Error promise rejection captured',
                ],
                denyUrls: [
                    // Chrome extensions
                    /extensions\//i,
                    /^chrome:\/\//i,
                ],
            });
        }
    }

    componentDidMount() {
        this.observeAmplitudeBanner();
        this.syncAmplitude();
        this.initCanny();
    }

    componentDidUpdate() {
        this.syncAmplitude();
        this.initCanny();
    }

    componentWillUnmount() {
        if (this._bannerResizeObserver) {
            this._bannerResizeObserver.disconnect();
        }
        if (this._bannerMutationObserver) {
            this._bannerMutationObserver.disconnect();
        }
        if (this._bannerUpdateFrame) {
            cancelAnimationFrame(this._bannerUpdateFrame);
        }
        document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
    }

    observeAmplitudeBanner() {
        if (
            typeof ResizeObserver === 'undefined' ||
            typeof MutationObserver === 'undefined'
        ) {
            return;
        }

        const updateBannerHeight = () => {
            this._bannerUpdateFrame = null;

            const banner = document.querySelector(BANNER_SELECTOR);

            if (banner !== this._observedBanner) {
                if (this._observedBanner) {
                    this._bannerResizeObserver.unobserve(this._observedBanner);
                }
                if (banner) {
                    this._bannerResizeObserver.observe(banner);
                }
                this._observedBanner = banner;
            }

            const height = banner ? banner.getBoundingClientRect().height : 0;

            if (height === this._bannerHeight) {
                return;
            }
            this._bannerHeight = height;

            document.documentElement.style.setProperty(
                BANNER_HEIGHT_VAR,
                `${height}px`
            );
        };

        const scheduleUpdate = () => {
            if (this._bannerUpdateFrame) {
                return;
            }
            this._bannerUpdateFrame = requestAnimationFrame(updateBannerHeight);
        };

        this._bannerResizeObserver = new ResizeObserver(scheduleUpdate);
        this._bannerMutationObserver = new MutationObserver(mutations => {
            if (mutations.some(touchesBanner)) {
                scheduleUpdate();
            }
        });
        this._bannerMutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });

        updateBannerHeight();
    }

    startAmplitude(userId) {
        if (this._amplitudeStarted) {
            return true;
        }

        if (!window.amplitude) {
            this.loadAmplitudeSdk();
            return false;
        }

        try {
            if (window.sessionReplay) {
                window.amplitude.add(
                    window.sessionReplay.plugin({
                        sampleRate: SESSION_REPLAY_SAMPLE_RATE,
                    })
                );
            }

            window.amplitude.init(AMPLITUDE_API_KEY, userId, {
                fetchRemoteConfig: true,
                autocapture: true,
                optOut: false,
            });
        } catch (error) {
            return false;
        }

        this._amplitudeStarted = true;
        this.startEngagement();

        return true;
    }

    loadAmplitudeSdk() {
        if (this._amplitudeSdkRequested) {
            return;
        }
        this._amplitudeSdkRequested = true;

        loadScript(AMPLITUDE_SCRIPT_URL, this.syncAmplitude, () => {
            this._amplitudeSdkRequested = false;
        });
    }

    startEngagement() {
        if (window.engagement) {
            window.amplitude.add(window.engagement.plugin());
            return;
        }

        loadScript(AMPLITUDE_ENGAGEMENT_SCRIPT_URL, () =>
            window.amplitude.add(window.engagement.plugin())
        );
    }

    clearAmplitudeIdentity() {
        if (this._amplitudeStarted) {
            if (this._amplitudeUserId && window.amplitude.reset) {
                window.amplitude.reset();
            }

            if (!this._amplitudeOptedOut && window.amplitude.setOptOut) {
                window.amplitude.setOptOut(true);
                this._amplitudeOptedOut = true;
            }
        }

        this._amplitudeUserId = undefined;
        this._amplitudeContext = undefined;
    }

    syncAmplitude() {
        const { auth = null, settings = null } = this.props;

        if (!auth || !auth.isLoggedIn) {
            this.clearAmplitudeIdentity();
            return;
        }

        const isImpersonating = !!auth.isImpersonating;
        const account = (settings && settings.settings) || null;
        const isAuthed = !isImpersonating && !!(account && account.email);

        const userId = this.getUserId(isAuthed, isImpersonating, auth);

        if (!userId) {
            this.clearAmplitudeIdentity();
            return;
        }

        const isInternal = isImpersonating || !!auth.isInternal;
        const properties = isAuthed
            ? {
                  email: account.email,
                  account_type: account.account_type,
                  company: account.company,
                  trial_end_date: account.trial_end_date || false,
                  sub_account_email: auth.userCredentialsEmail || undefined,
                  is_internal: isInternal,
                  is_impersonating: false,
                  impersonating_account_id: undefined,
              }
            : {
                  is_internal: true,
                  is_impersonating: true,
                  impersonating_account_id: auth.accountId,
              };

        const context = JSON.stringify([userId, properties]);

        if (context === this._amplitudeContext) {
            return;
        }

        if (!this.startAmplitude(userId)) {
            return;
        }
        this._amplitudeContext = context;

        if (this._amplitudeOptedOut) {
            if (window.amplitude.setOptOut) {
                window.amplitude.setOptOut(false);
            }
            this._amplitudeOptedOut = false;
        }

        if (!window.amplitude.setUserId) {
            return;
        }

        if (userId !== this._amplitudeUserId) {
            this._amplitudeUserId = userId;
            window.amplitude.setUserId(userId);
        }

        if (window.amplitude.Identify && window.amplitude.identify) {
            identifyUser(properties);
        }

        if (isAuthed && account.company && window.amplitude.setGroup) {
            window.amplitude.setGroup('company', account.company);
        }
    }

    async initCanny() {
        const { auth = null, settings = null } = this.props;
        const account = (settings && settings.settings) || null;
        const cannyButtons = Array.from(
            document.querySelectorAll('[data-canny-changelog]')
        ).filter(button => !this._cannyButtons.has(button));

        const isAuthed =
            auth && auth.isLoggedIn && settings && settings.settings.email;

        const isBlockingAccount =
            (auth && auth.role === 'blocking') ||
            (account && account.account_type === 'blocking');

        if (
            this._cannyPending ||
            !isAuthed ||
            !isBlockingAccount ||
            typeof window === 'undefined' ||
            !window.Canny ||
            cannyButtons.length === 0
        ) {
            return;
        }

        this._cannyPending = true;

        try {
            // Fetch SSO token from the API to initialize private Canny changelog.
            const ssoToken = this._cannyToken || (await getCannyToken());

            if (!ssoToken) {
                return;
            }

            this._cannyToken = ssoToken;

            cannyButtons.forEach(cannyButton => {
                window.Canny(
                    'initChangelog',
                    {
                        appID: '6a19b8d71c58e6563a8fee0e',
                        ssoToken,
                        position: 'bottom',
                        align: 'right',
                        theme: 'light',
                    },
                    cannyButton
                );

                this._cannyButtons.add(cannyButton);
            });
        } catch (error) {
            console.error('App.initCanny: Error initializing Canny:', error);
        } finally {
            this._cannyPending = false;
        }
    }

    getUserId(isAuthed, isImpersonating, auth) {
        if (isAuthed) {
            return auth.userCredentialsId
                ? `cred-${auth.userCredentialsId}`
                : auth.accountId
                ? `acct-${auth.accountId}`
                : null;
        } else if (isImpersonating && auth.impersonatorAccountId) {
            // tag the internal staff member's own id, never the
            // impersonated client's, so impersonated activity is
            // recorded without polluting the client's own stats
            return `acct-${auth.impersonatorAccountId}`;
        }
    }

    render() {
        return (
            <BlockUi
                tag="div"
                blocking={this.props.isApiCallInProgress}
                loader={<Loader active color="#02a17c" />}
            >
                <Main />
            </BlockUi>
        );
    }
}

const mapStateToProps = state => {
    return {
        isApiCallInProgress: state.apiCallStatus.isApiCallInProgress,
        auth: state.authReducer,
        settings: state.settings,
    };
};

export default withRouter(connect(mapStateToProps, null)(App));
